import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { trackPageVisit, trackAction } from '../utils/tracking';
import './VotingMachine.css';

const VotingMachine = () => {
  const [votedCandidates, setVotedCandidates] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentVotedCandidate, setCurrentVotedCandidate] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [, setVoteCounts] = useState({
    'ward-4': { 'candidate-1': 1331 }
  });

  // Track page visit
  useEffect(() => {
    trackPageVisit('/voting');
  }, []);

  // Load voices when component mounts - improved loading
  useEffect(() => {
    const loadVoices = () => {
      if ('speechSynthesis' in window) {
        // Force voice loading
        const voices = window.speechSynthesis.getVoices();
        console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
      }
    };
    
    // Load voices immediately
    loadVoices();
    
    // Chrome/Edge need this event to load voices
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
      
      // Also try loading after a short delay (for some browsers)
      setTimeout(loadVoices, 100);
    }
    
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);


  // Sample candidate data - can be replaced with API data
  const wards = [
    {
      id: 'ward-4',
      number: 4,
      division: '14 ड',
      party: 'महाराष्ट्र नवनिर्माण सेना',
      candidates: [
        { id: 'candidate-1', name: '-', photo: null, symbol: null, hasVote: false },
        { id: 'candidate-2', name: '-', photo: null, symbol: null, hasVote: false },
        { id: 'candidate-3', name: 'राजेंद्र (बाबु) वागसकर', photo: '/1.jpeg', symbol: '/mns-symbol.png', hasVote: true }
      ]
    }
  ];

  const playVoteAudio = (ward, candidate, serialNumber) => {
    // Map candidate names to audio files
    const audioFileMap = {
      'स्वाती भास्कर जगताप (भिसे)': '/swati.wav',
      'प्रदीप मंतुआ परदेशी': '/pradeep.wav',
      'सौ. गौरी अक्षय पिंगळे': '/gauri.wav',
      'राजेंद्र (बाबु) वागसकर': '/rajendra.mp3'
    };
    
    // Get the audio file for this candidate
    const candidateName = candidate.name.trim();
    const audioFile = audioFileMap[candidateName];
    
    if (audioFile) {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      // Play the pre-recorded audio file
      const audio = new Audio(audioFile);
      setCurrentAudio(audio);
      
      audio.onerror = () => {
        console.error('Error loading audio file:', audioFile);
        setCurrentAudio(null);
        // Fallback to text-to-speech if audio file fails
        playTextToSpeech(ward, candidate, serialNumber);
      };
      
      audio.onended = () => {
        console.log('Vote audio playback completed');
        setCurrentAudio(null);
      };
      
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setCurrentAudio(null);
        // Fallback to text-to-speech if play fails
        playTextToSpeech(ward, candidate, serialNumber);
      });
    } else {
      // If no audio file found, use text-to-speech
      console.log('No audio file found for candidate:', candidateName);
      playTextToSpeech(ward, candidate, serialNumber);
    }
  };

  const playTextToSpeech = (ward, candidate, serialNumber) => {
    // Get party name
    const partyName = ward.party || 'महाराष्ट्र नवनिर्माण सेना';
    
    // Get symbol name based on symbol file
    let symbolName = 'चिन्ह';
    if (candidate.symbol) {
      if (candidate.symbol.includes('congress-symbol')) {
        symbolName = 'हाताचा पंजा';
      } else if (candidate.symbol.includes('flaming-torch')) {
        symbolName = 'मशाल';
      } else if (candidate.symbol.includes('mns-symbol')) {
        symbolName = 'रेल्वे इंजिन';
      }
    }
    
    // Get division letter (extract from division like "14 अ" -> "अ")
    const divisionLetter = ward.division.split(' ').pop() || '';
    
    // Clean candidate name (remove extra spaces, handle parentheses)
    let candidateName = candidate.name.trim();
    
    // Generate the audio message
    const audioMessage = `${partyName}चे प्रभाग क्रमांक चौदा – ${divisionLetter} मधील अधिकृत उमेदवार, ${candidateName} यांच्यासाठी ${symbolName} या क्रमांकासमोरील बटन दाबून डेमो मतदान केल्याबद्दल धन्यवाद.`;
    
    // Use Web Speech API (browser text-to-speech) as fallback
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(audioMessage);
      
      const voices = window.speechSynthesis.getVoices();
      
      // Try to find Hindi voice (better quality for Marathi text)
      let selectedVoice = voices.find(voice => 
        voice.lang === 'hi-IN' || voice.lang === 'hi'
      ) || voices.find(voice => 
        voice.name.toLowerCase().includes('female')
      );
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang || 'hi-IN';
      } else {
        utterance.lang = 'hi-IN';
      }
      
      utterance.rate = 0.85;
      utterance.pitch = 1.0;
      utterance.volume = 1;
      
      utterance.onend = () => {
        console.log('Text-to-speech completed');
      };
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleVote = (wardId, candidateId) => {
    if (votedCandidates[wardId]) {
      alert('आपण या प्रभागासाठी आधीच मतदान केले आहे!');
      return;
    }

    // Find the candidate and ward details
    const ward = wards.find(w => w.id === wardId);
    const candidate = ward?.candidates.find(c => c.id === candidateId);

    // Track vote action with detailed information
    // Using 'vote' as action so it's easier to filter
    console.log('🔵 Vote button clicked!', { wardId, candidateId, candidateName: candidate?.name });
    
    trackAction('/voting', 'vote', {
      wardId,
      wardNumber: ward?.number,
      wardDivision: ward?.division,
      candidateId,
      candidateName: candidate?.name,
      candidateSerialNumber: ward?.candidates.findIndex(c => c.id === candidateId) + 1,
      party: ward?.party,
      symbol: candidate?.symbol,
    }).catch(err => {
      console.error('❌ Failed to track vote:', err);
    });

    setVotedCandidates(prev => ({
      ...prev,
      [wardId]: candidateId
    }));

    // Increment vote count
    setVoteCounts(prev => ({
      ...prev,
      [wardId]: {
        ...prev[wardId],
        [candidateId]: (prev[wardId]?.[candidateId] || 0) + 1
      }
    }));

    // Show success message immediately with candidate details
    if (candidate && ward) {
      // Find the candidate's position/index in the candidates array
      const candidateIndex = ward.candidates.findIndex(c => c.id === candidateId);
      const serialNumber = candidateIndex + 1;
      
      setCurrentVotedCandidate({
        name: candidate.name,
        division: ward.division,
        wardNumber: ward.number,
        serialNumber: serialNumber,
        photo: candidate.photo,
        symbol: candidate.symbol,
        party: ward.party || 'महाराष्ट्र नवनिर्माण सेना'
      });
      
      // Play audio message
      playVoteAudio(ward, candidate, serialNumber);
      
      setTimeout(() => {
        setShowSuccess(true);
      }, 300);
    }
  };



  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setCurrentVotedCandidate(null);
  };

  const siteUrl = window.location.origin;
  // Use absolute URL for OG image to ensure it's picked up correctly
  const ogImage = `${siteUrl}/banner1.jpg`;
  const ogUrl = `${siteUrl}/voting`;
  
  console.log('VotingMachine OG Image:', ogImage);
  console.log('VotingMachine OG URL:', ogUrl);

  return (
    <>
      <Helmet>
        <title>डिजिटल डमी मतदान यंत्र - पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६</title>
        <meta name="description" content="डिजिटल डमी मतदान यंत्र - पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६ | प्रभाग क्र. १४ : कोरेगांव पार्क–घोरपडी–मुंढवा" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={ogUrl} />
        <meta property="og:title" content="डिजिटल डमी मतदान यंत्र - पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६" />
        <meta property="og:description" content="प्रभाग क्र. १४ : कोरेगांव पार्क–घोरपडी–मुंढवा | डिजिटल डमी मतदान यंत्र | महाराष्ट्र नवनिर्माण सेना" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:url" content={ogImage} />
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${siteUrl}/voting`} />
        <meta name="twitter:title" content="डिजिटल डमी मतदान यंत्र - पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६" />
        <meta name="twitter:description" content="प्रभाग क्र. १४ : कोरेगांव पार्क–घोरपडी–मुंढवा | डिजिटल डमी मतदान यंत्र | महाराष्ट्र नवनिर्माण सेना" />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      
      <div className="voting-machine">
      <div className="voting-header">
        <h1 className="voting-title">पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६ - डिजिटल मतदान यंत्र</h1>
      </div>

      <div className="voting-instructions">
        <h2 className="instructions-title">मतदान कसे करावे याचे प्रात्यक्षिक</h2>
        <div className="instructions-content">
          <p><strong>१.</strong> खाली दिलेल्या तक्त्यामध्ये आपल्या इच्छित उमेदवाराची माहिती तपासा.</p>
          <p><strong>२.</strong> उमेदवाराच्या फोटो, चिन्ह आणि नावाची खात्री करा.</p>
          <p><strong>३.</strong> उमेदवाराच्या समोरील <strong>"मतदान करा"</strong> बटनावर क्लिक करा.</p>
          <p><strong>४.</strong> मतदान केल्यानंतर आपल्याला एक पुष्टीकरण संदेश दिसेल.</p>
          <p><strong>५.</strong> एका प्रभागासाठी फक्त एकदाच मतदान करता येते.</p>
        </div>
      </div>

      {showSuccess && currentVotedCandidate && (
        <div className="popup-overlay" onClick={handleCloseSuccess} style={{ display: showSuccess ? 'flex' : 'none' }}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <div className="popup-confetti" id="confetti-container"></div>
              <div className="popup-title">{currentVotedCandidate.name}</div>
            </div>
            
            <div className="popup-body">
              <div className="popup-row">
                <span className="label">पक्ष</span>
                <span className="value">{currentVotedCandidate.party}</span>
              </div>
              
              <div className="popup-row">
                <span className="label">क्रमांक</span>
                <span className="value">{currentVotedCandidate.serialNumber || currentVotedCandidate.wardNumber}</span>
              </div>
              
              <div className="popup-row">
                <span className="label">उमेदवार</span>
                <span className="value">{currentVotedCandidate.name}</span>
              </div>
              
              <div className="popup-images">
                <div className="popup-img-block">
                  <div>फोटो</div>
                  <img src={currentVotedCandidate.photo} alt={currentVotedCandidate.name} style={{ display: 'block' }} />
                </div>
                <div className="popup-img-block">
                  <div>चिन्ह</div>
                  <img src={currentVotedCandidate.symbol} alt="Symbol" style={{ display: 'block' }} />
                </div>
              </div>
            </div>
            
            <div className="popup-footer">
              <div className="popup-footer-text">आपले मत महत्त्वाचे आहे. 🙏</div>
            </div>
          </div>
        </div>
      )}

      <div className="voting-content">
        {wards.map((ward) => (
          <div key={ward.id} className="ward-section">
            <p className="ward-info">
              आता आपण <strong>{ward.division}</strong> मधील नगरसेवकासाठी मतदान करत आहेत.
            </p>
            <p className="ward-party">पक्ष: {ward.party || 'महाराष्ट्र नवनिर्माण सेना'} | प्रभाग: <strong>{ward.division}</strong></p>

            <div className="candidates-table-wrapper">
              <table className="candidates-table">
                <thead>
                  <tr>
                    <th>क्र.</th>
                    <th>उमेदवार</th>
                    <th>फोटो</th>
                    <th>चिन्ह</th>
                    <th>मतदान</th>
                  </tr>
                </thead>
                <tbody>
                  {ward.candidates.map((candidate, index) => (
                    <tr key={candidate.id} className={candidate.name === '-' ? 'empty-row' : ''}>
                      <td>{index + 1}</td>
                      <td>{candidate.name}</td>
                      <td>
                        {candidate.photo ? (
                          <img src={candidate.photo} alt={candidate.name} className="candidate-photo" />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {candidate.symbol ? (
                          <img src={candidate.symbol} alt="चिन्ह" className="candidate-symbol" />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {candidate.hasVote ? (
                          <button
                            className={`vote-btn ${votedCandidates[ward.id] === candidate.id ? 'voted' : ''} ${votedCandidates[ward.id] && votedCandidates[ward.id] !== candidate.id ? 'disabled' : ''}`}
                            onClick={() => handleVote(ward.id, candidate.id)}
                            disabled={votedCandidates[ward.id] && votedCandidates[ward.id] !== candidate.id}
                          >
                            {votedCandidates[ward.id] === candidate.id ? (
                              '✓ मतदान केले'
                            ) : (
                              <>
                                <span className="vote-btn-indicator">👆</span>
                                <span className="vote-btn-text">मतदान करा</span>
                                <span className="vote-btn-click-here">(येथे क्लिक करा)</span>
                              </>
                            )}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
    </>
  );
};

export default VotingMachine;
